import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Search, Plus, Clock, Car, Mail, Building2,
  ChevronLeft, ChevronRight, Zap, Timer, CheckCircle, AlertTriangle, Trash2, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESPONSE_STATUS_STYLES = {
  sent: 'bg-green-500/10 text-green-400 border-green-500/20',
  pending_review: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  no_template: 'bg-red-500/10 text-red-400 border-red-500/20',
  rejected: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  error: 'bg-red-600/10 text-red-500 border-red-600/20',
  failed_no_email: 'bg-red-500/10 text-red-400 border-red-500/20',
  call_logged: 'bg-green-500/10 text-green-400 border-green-500/20',
  pending_call_log: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  logging_call: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

function formatResponseTime(seconds) {
  if (!seconds) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

export default function ResponseLogPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dealerships, setDealerships] = useState([]);
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem('responseLogFilters');
      return saved ? JSON.parse(saved) : { dealership_id: '', lead_type: '', search: '' };
    } catch { return { dealership_id: '', lead_type: '', search: '' }; }
  });
  const [page, setPage] = useState(() => {
    try {
      const saved = sessionStorage.getItem('responseLogPage');
      return saved ? parseInt(saved, 10) : 0;
    } catch { return 0; }
  });
  const [showSimulate, setShowSimulate] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simForm, setSimForm] = useState({
    dealership_id: '', email_subject: '', email_body: '', sender_email: '', alias_email: ''
  });
  const [sendToCustom, setSendToCustom] = useState(false);
  const [customTestEmail, setCustomTestEmail] = useState('');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [clearing, setClearing] = useState(false);
  const [previewLead, setPreviewLead] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(-1);
  const [pendingPreviewPos, setPendingPreviewPos] = useState(null); // 'first' or 'last' — set when crossing page boundary
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const LIMIT = 20;

  // Persist filters and page to sessionStorage
  useEffect(() => { sessionStorage.setItem('responseLogFilters', JSON.stringify(filters)); }, [filters]);
  useEffect(() => { sessionStorage.setItem('responseLogPage', String(page)); }, [page]);

  const clearLog = async () => {
    if (!window.confirm('Are you sure you want to clear all leads from the response log? This cannot be undone.')) return;
    setClearing(true);
    try {
      const res = await axios.delete(`${API}/leads/clear`, { headers, withCredentials: true });
      toast.success(`Cleared ${res.data.deleted_count} leads`);
      setPage(0);
      setLeads([]);
      setTotal(0);
      await fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear log');
    }
    setClearing(false);
  };

  useEffect(() => {
    const fetchDealerships = async () => {
      try {
        const res = await axios.get(`${API}/dealerships`, { headers, withCredentials: true });
        setDealerships(res.data);
      } catch (err) { console.error('Failed to fetch dealerships:', err); }
    };
    const fetchTestEmail = async () => {
      try {
        const res = await axios.get(`${API}/settings`, { headers, withCredentials: true });
        if (res.data.simulate_test_email) {
          setCustomTestEmail(res.data.simulate_test_email);
        }
      } catch (err) { console.error('Failed to fetch settings:', err); }
    };
    fetchDealerships();
    fetchTestEmail();
  }, [token]);

  const fetchLeads = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { limit: LIMIT, skip: page * LIMIT };
      if (filters.dealership_id) params.dealership_id = filters.dealership_id;
      if (filters.lead_type) params.lead_type = filters.lead_type;
      if (filters.search) params.search = filters.search;
      const res = await axios.get(`${API}/leads`, { headers, params, withCredentials: true });
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch (err) { console.error('Failed to fetch leads:', err); }
    if (!silent) setLoading(false);
    // Restore scroll position after leads load
    const savedScroll = sessionStorage.getItem('responseLogScroll');
    if (savedScroll) {
      requestAnimationFrame(() => window.scrollTo(0, parseInt(savedScroll, 10)));
      sessionStorage.removeItem('responseLogScroll');
    }
  };

  // Debounced search
  const searchTimerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search || '');
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setFilters(f => ({ ...f, search: debouncedSearch }));
      setPage(0);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchLeads();
  }, [filters.dealership_id, filters.lead_type, filters.search, page]);

  // Only refresh when new leads arrive (check count silently every 10s)
  useEffect(() => {
    let lastTotal = total;
    const interval = setInterval(async () => {
      try {
        const params = { limit: 1, skip: 0 };
        if (filters.dealership_id) params.dealership_id = filters.dealership_id;
        if (filters.lead_type) params.lead_type = filters.lead_type;
        const res = await axios.get(`${API}/leads`, { headers, params, withCredentials: true });
        const newTotal = res.data.total;
        if (newTotal !== lastTotal) {
          lastTotal = newTotal;
          fetchLeads(true);
        }
      } catch (err) { console.error('Failed to check for new leads:', err); }
    }, 10000);
    return () => clearInterval(interval);
  }, [filters.dealership_id, filters.lead_type, page, total]);

  const handleSimulate = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(simForm.sender_email.trim())) {
      alert('Please enter a valid email address in the Sender Email field.');
      return;
    }
    if (sendToCustom && !emailRegex.test(customTestEmail.trim())) {
      alert('Please enter a valid custom email address.');
      return;
    }
    if (smsEnabled && !smsPhone.trim()) {
      alert('Please enter a phone number for SMS.');
      return;
    }
    setSimulating(true);
    try {
      const payload = {
        ...simForm,
        send_to: sendToCustom ? customTestEmail.trim() : 'original',
        sms_enabled: smsEnabled,
        sms_phone: smsEnabled ? smsPhone.trim() : null
      };
      const res = await axios.post(`${API}/leads/simulate`, payload, { headers, withCredentials: true, timeout: 120000 });
      // Save custom email for future use
      if (sendToCustom && customTestEmail.trim()) {
        axios.put(`${API}/settings/test-email`, { test_email: customTestEmail.trim() }, { headers, withCredentials: true }).catch(() => {});
      }
      setShowSimulate(false);
      setSimForm({ dealership_id: '', email_subject: '', email_body: '', sender_email: '', alias_email: '' });
      setSmsEnabled(false);
      setSmsPhone('');
      setPage(0);
      await fetchLeads();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to simulate lead');
    }
    setSimulating(false);
  };

  const filteredLeads = leads;

  const sortedDealerships = useMemo(() =>
    [...dealerships].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [dealerships]
  );

  const totalPages = Math.ceil(total / LIMIT);

  // After page loads, if we were navigating across a page boundary, open the preview
  useEffect(() => {
    if (pendingPreviewPos && !loading && filteredLeads.length > 0) {
      if (pendingPreviewPos === 'first') {
        setPreviewIndex(0);
        setPreviewLead(filteredLeads[0]);
      } else if (pendingPreviewPos === 'last') {
        const lastIdx = filteredLeads.length - 1;
        setPreviewIndex(lastIdx);
        setPreviewLead(filteredLeads[lastIdx]);
      }
      setPendingPreviewPos(null);
    }
  }, [loading, filteredLeads, pendingPreviewPos]);

  const globalIndex = page * LIMIT + previewIndex;
  const navigatePreview = (direction) => {
    const nextIdx = previewIndex + direction;
    if (nextIdx >= 0 && nextIdx < filteredLeads.length) {
      setPreviewIndex(nextIdx);
      setPreviewLead(filteredLeads[nextIdx]);
    } else if (direction > 0 && page < totalPages - 1) {
      setPage(p => p + 1);
      setPendingPreviewPos('first');
    } else if (direction < 0 && page > 0) {
      setPage(p => p - 1);
      setPendingPreviewPos('last');
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="response-log-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight">
            Response Log
          </h1>
          <p className="text-slate-400 text-sm mt-2">{total} leads processed</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={clearLog}
            disabled={clearing || total === 0}
            variant="outline"
            className="border-red-500/30 bg-red-500/5 hover:bg-red-500/10 text-red-400 font-bold text-xs tracking-widest uppercase"
            data-testid="clear-log-btn"
          >
            {clearing ? (
              <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" /> Clear Log
              </>
            )}
          </Button>
          <Dialog open={showSimulate} onOpenChange={setShowSimulate}>
            <DialogTrigger asChild>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase hover:shadow-[0_0_15px_rgba(220,38,38,0.4)] transition-all"
                data-testid="simulate-lead-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Simulate Lead
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider flex items-center gap-2">
                <Zap className="w-5 h-5 text-red-500" /> Simulate Incoming Lead
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-400">
              Simulates an email lead arriving from a CRM. The system will AI-parse it, auto-select a template, and generate a response.
            </p>
            <form onSubmit={handleSimulate} className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Dealership</Label>
                <Select value={simForm.dealership_id} onValueChange={v => {
                  const dlr = dealerships.find(d => d.dealership_id === v);
                  const isVS = (dlr?.crm || '').toLowerCase() === 'vinsolutions';
                  const prefillEmail = isVS
                    ? 'smart@bounces.motosnap.com'
                    : (dlr?.reply_to_email || dlr?.email_alias || '');
                  setSimForm(p => ({ ...p, dealership_id: v, sender_email: prefillEmail, alias_email: '' }));
                }}>
                  <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11" data-testid="sim-dealership-select">
                    <SelectValue placeholder="Select dealership" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626]">
                    {dealerships.map(d => (
                      <SelectItem key={d.dealership_id} value={d.dealership_id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                const dlr = dealerships.find(d => d.dealership_id === simForm.dealership_id);
                const isVS = (dlr?.crm || '').toLowerCase() === 'vinsolutions';
                const aliases = Array.isArray(dlr?.email_aliases) ? dlr.email_aliases : [];
                if (!isVS || aliases.length === 0) return null;
                return (
                  <div>
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">
                      VinSolutions Alias <span className="text-slate-600 normal-case tracking-normal">(route lead to a specific sub-dealership)</span>
                    </Label>
                    <Select
                      value={simForm.alias_email || 'none'}
                      onValueChange={v => setSimForm(p => ({ ...p, alias_email: v === 'none' ? '' : v }))}
                    >
                      <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11" data-testid="sim-alias-select">
                        <SelectValue placeholder="Default (no alias — uses dealership top-level)" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#171717] border-[#262626]">
                        <SelectItem value="none">Default (no alias)</SelectItem>
                        {aliases.map(a => (
                          <SelectItem key={a.alias} value={a.alias}>
                            {a.dealership_name || a.alias}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })()}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Sender Email (CRM Routing Address)</Label>
                <Input
                  type="email"
                  value={simForm.sender_email}
                  readOnly
                  placeholder="Select a dealership above"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm font-mono text-slate-400 cursor-not-allowed opacity-75"
                  data-testid="sim-sender-email"
                  required
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Email Subject</Label>
                <Input
                  value={simForm.email_subject}
                  onChange={e => setSimForm(p => ({ ...p, email_subject: e.target.value }))}
                  placeholder="Interested in 2024 Toyota Camry"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="sim-email-subject"
                  required
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Email Body</Label>
                <Textarea
                  value={simForm.email_body}
                  onChange={e => setSimForm(p => ({ ...p, email_body: e.target.value }))}
                  placeholder="Hi, I'm interested in the 2024 Toyota Camry XSE. Could you provide pricing? I'd like to schedule a test drive. My budget is around $30,000. Thanks, John Smith (555-123-4567)"
                  className="mt-1.5 bg-[#111] border-[#333] text-sm placeholder:text-slate-600 min-h-[120px]"
                  data-testid="sim-email-body"
                  required
                />
              </div>
              <div className="p-3 bg-[#111] border border-[#262626] rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Send Response To</Label>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold tracking-wider uppercase ${!sendToCustom ? 'text-white' : 'text-slate-500'}`}>Original</span>
                    <Switch
                      checked={sendToCustom}
                      onCheckedChange={setSendToCustom}
                      data-testid="sim-send-to-toggle"
                    />
                    <span className={`text-[10px] font-bold tracking-wider uppercase ${sendToCustom ? 'text-white' : 'text-slate-500'}`}>Custom</span>
                  </div>
                </div>
                {sendToCustom && (
                  <Input
                    type="email"
                    value={customTestEmail}
                    onChange={e => setCustomTestEmail(e.target.value)}
                    placeholder="your-email@example.com"
                    className="bg-[#0A0A0A] border-[#333] h-10 text-sm placeholder:text-slate-600"
                    data-testid="sim-custom-email"
                    required
                  />
                )}
                <p className="text-[10px] text-slate-500">
                  {sendToCustom ? 'Response will be sent to the custom email above.' : 'Response will be sent back to the CRM / original sender.'}
                </p>
              </div>
              <div className="p-3 bg-[#111] border border-[#262626] rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Send SMS</Label>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold tracking-wider uppercase ${!smsEnabled ? 'text-white' : 'text-slate-500'}`}>Off</span>
                    <Switch
                      checked={smsEnabled}
                      onCheckedChange={setSmsEnabled}
                      data-testid="sim-sms-toggle"
                    />
                    <span className={`text-[10px] font-bold tracking-wider uppercase ${smsEnabled ? 'text-white' : 'text-slate-500'}`}>On</span>
                  </div>
                </div>
                {smsEnabled && (
                  <Input
                    type="tel"
                    value={smsPhone}
                    onChange={e => setSmsPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-[#0A0A0A] border-[#333] h-10 text-sm placeholder:text-slate-600"
                    data-testid="sim-sms-phone"
                  />
                )}
                <p className="text-[10px] text-slate-500">
                  {smsEnabled ? 'An SMS will be sent to the phone number above with vehicle details.' : 'Toggle on to also send an SMS text message.'}
                </p>
              </div>
              <Button
                type="submit"
                disabled={simulating || !simForm.dealership_id}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11"
                data-testid="sim-submit-btn"
              >
                {simulating ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    AI Processing & Auto-Responding...
                  </div>
                ) : (
                  <><Zap className="w-4 h-4 mr-2" /> Process Lead</>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#0A0A0A] border-[#262626] p-4 mb-6" data-testid="response-filters">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={debouncedSearch}
              onChange={e => setDebouncedSearch(e.target.value)}
              placeholder="Search name, email, VIN, vehicle, dealership..."
              className="pl-9 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600"
              data-testid="search-responses-input"
            />
          </div>
          <Select value={filters.dealership_id} onValueChange={v => setFilters(p => ({ ...p, dealership_id: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[180px] bg-[#111] border-[#333] h-9 text-sm" data-testid="filter-dealership">
              <SelectValue placeholder="All Dealerships" />
            </SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626]">
              <SelectItem value="all">All Dealerships</SelectItem>
              {sortedDealerships.map(d => (
                <SelectItem key={d.dealership_id} value={d.dealership_id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.lead_type} onValueChange={v => setFilters(p => ({ ...p, lead_type: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[180px] bg-[#111] border-[#333] h-9 text-sm" data-testid="filter-lead-type">
              <SelectValue placeholder="All Lead Types" />
            </SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626]">
              <SelectItem value="all">All Lead Types</SelectItem>
              <SelectItem value="new_vehicle_inquiry">New Vehicle</SelectItem>
              <SelectItem value="new_vehicle_inquiry_vin">New Vehicle (VIN)</SelectItem>
              <SelectItem value="used_vehicle_inquiry">Used Vehicle</SelectItem>
              <SelectItem value="used_vehicle_inquiry_cpo">Used Vehicle (CPO)</SelectItem>
              <SelectItem value="general_inquiry">General Inquiry</SelectItem>
              <SelectItem value="service_appointment">Service</SelectItem>
              <SelectItem value="onstar_service">OnStar Service</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Response list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredLeads.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-[#262626] p-12 text-center">
          <Zap className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <h3 className="font-heading text-xl font-semibold text-white uppercase mb-2">No Responses Yet</h3>
          <p className="text-slate-500 text-sm">
            {dealerships.length === 0
              ? "Add a dealership and seed templates, then simulate your first lead."
              : "Simulate a lead to see the auto-response engine in action."}
          </p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="response-list">
          {filteredLeads.map((lead, i) => (
            <Card
              key={lead.lead_id}
              onClick={(e) => {
                if (e.defaultPrevented) return;
                sessionStorage.setItem('responseLogScroll', String(window.scrollY));
                navigate(`/responses/${lead.lead_id}`);
              }}
              className="bg-[#0A0A0A] border-[#262626] p-5 hover:border-[#404040] cursor-pointer transition-all duration-200 animate-fade-up"
              style={{ animationDelay: `${i * 40}ms` }}
              data-testid={`response-card-${lead.lead_id}`}
            >
              <div className="flex items-center gap-4">
                {/* Status indicator */}
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  lead.response_status === 'sent' || lead.response_status === 'call_logged' ? 'bg-green-500' :
                  lead.response_status === 'pending_review' || lead.response_status === 'pending_call_log' || lead.response_status === 'logging_call' ? 'bg-orange-500' : 'bg-red-500'
                }`} />

                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-medium text-white">{lead.customer_name || 'Unknown'}</span>
                    <Badge variant="outline" className="text-[9px] font-bold tracking-wider border-[#333] text-slate-400 uppercase">
                      {lead.lead_type_label || lead.lead_type}
                    </Badge>
                    {(lead.source === 'simulated' || (lead.gmail_message_id && lead.gmail_message_id.startsWith('sim_'))) && (
                      <Badge className="text-[9px] font-bold tracking-wider uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        Simulated Lead
                      </Badge>
                    )}
                    <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${RESPONSE_STATUS_STYLES[lead.response_status] || ''}`}>
                      {lead.response_status === 'pending_review' ? 'Pending Review' :
                       lead.response_status === 'no_template' ? 'No Template' :
                       lead.response_status === 'error' ? 'Processing Error' :
                       lead.response_status === 'failed_no_email' ? 'Failed, No Email' :
                       lead.response_status === 'call_logged' ? 'Call Logged' :
                       lead.response_status === 'pending_call_log' ? 'Pending Call Log' :
                       lead.response_status === 'logging_call' ? 'Logging Call' :
                       lead.response_status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> {lead.dealership_name}
                    </span>
                    {lead.vehicle_make && (
                      <span className="flex items-center gap-1">
                        <Car className="w-3 h-3" />
                        {[lead.vehicle_year, lead.vehicle_make, lead.vehicle_model, lead.vehicle_trim].filter(Boolean).join(' ')}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {lead.sender_email}
                    </span>
                  </div>
                </div>

                {/* Timing + Preview */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  {lead.response_body && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 hover:text-white h-8 px-2.5"
                      title="View Response"
                      data-testid={`view-response-${lead.lead_id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPreviewLead(lead); setPreviewIndex(i); }}
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Timer className="w-3 h-3" />
                      <span className="font-mono font-bold">{formatResponseTime(lead.response_time_seconds)}</span>
                    </div>
                    <span className="text-[10px] text-slate-600">
                      {lead.received_at ? new Date(lead.received_at).toLocaleString() : ''}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="text-slate-400 hover:text-white" data-testid="prev-page-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <span className="text-xs text-slate-500 font-bold tracking-wider">Page {page + 1} of {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="text-slate-400 hover:text-white" data-testid="next-page-btn">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
      {/* Response Preview Dialog */}
      <Dialog open={!!previewLead} onOpenChange={(open) => { if (!open) { setPreviewLead(null); setPreviewIndex(-1); } }}>
        <DialogContent
          className="bg-[#0A0A0A] border-[#262626] text-white max-w-[900px] w-[95vw] max-h-[85vh] overflow-y-auto [&>button.absolute]:hidden"
          data-testid="response-preview-dialog"
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') navigatePreview(-1);
            else if (e.key === 'ArrowRight') navigatePreview(1);
          }}
        >
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
                Auto-Generated Response
              </DialogTitle>
              <div className="flex items-center gap-2 mr-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={globalIndex <= 0}
                  onClick={() => navigatePreview(-1)}
                  className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 hover:text-white h-8 w-8 p-0"
                  data-testid="preview-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-slate-500 font-mono min-w-[60px] text-center">
                  {globalIndex + 1} / {total}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={globalIndex >= total - 1}
                  onClick={() => navigatePreview(1)}
                  className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 hover:text-white h-8 w-8 p-0"
                  data-testid="preview-next-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          {previewLead && (
            <div className="space-y-4 mt-2">
              <div className="p-4 bg-[#111] border border-[#262626] rounded-lg">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">To</p>
                  <p className="text-sm text-white">{previewLead.customer_name || 'Unknown'}</p>
                  <span className="text-[10px] text-slate-600">|</span>
                  <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Vehicle</p>
                  <p className="text-sm text-white">
                    {[previewLead.vehicle_year, previewLead.vehicle_make, previewLead.vehicle_model, previewLead.vehicle_trim].filter(Boolean).join(' ') || '--'}
                  </p>
                  <span className="text-[10px] text-slate-600">|</span>
                  <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Template</p>
                  <p className="text-sm text-white">{previewLead.template_name || '--'}</p>
                </div>
              </div>
              {previewLead.response_subject && (
                <div className="p-4 bg-[#111] border border-[#262626] rounded-lg">
                  <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">Subject</p>
                  <p className="text-sm text-white">{previewLead.response_subject}</p>
                </div>
              )}
              <div className="bg-white rounded-lg overflow-x-auto">
                <div
                  className="template-preview text-sm text-gray-800 leading-relaxed max-w-none break-words p-6 [&_img]:max-w-full [&_img]:h-auto"
                  style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewLead.response_body) }}
                  data-testid="response-preview-body"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
